/**
 * /conversations — Conversations Explorer (Story 7.10).
 *
 * Surface:
 *   - Sidebar filter panel (status multi-select, campaign, date range, phone,
 *     content search) with Apply / Clear.
 *   - Main table: masked phone, campaign, status badge, message count,
 *     lastMessageAt (relative time), row click → /conversations/:id.
 *   - Prev/Next pagination with "page X of Y".
 *
 * The page is viewer+ accessible. Row masking is enforced at the API
 * boundary (Story 7.10 AC Phone masking) — the UI only ever receives
 * `maskedPhone`.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ApiError, apiClient } from '@/lib/api';
import { formatDate } from '@/lib/format-date';
import {
  ConversationFilters,
  EMPTY_FILTERS,
  toQueryString,
  type ConversationFilterValue,
} from '@/components/conversations/ConversationFilters';
import { StatusBadge, type ConversationStatus } from '@/components/conversations/StatusBadge';

interface ConversationListItem {
  id: string;
  customerId: string;
  maskedPhone: string;
  campaignId: string;
  campaignName: string | null;
  status: ConversationStatus;
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
}

interface ListResponse {
  items: ConversationListItem[];
  total: number;
  page: number;
  pageSize: number;
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s atrás`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m atrás`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h atrás`;
  const diffDays = Math.round(diffSec / 86400);
  if (diffDays < 30) return `${diffDays}d atrás`;
  return formatDate(iso);
}

const DEFAULT_PAGE_SIZE = 20;

export function ConversationsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ConversationFilterValue>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const qs = toQueryString(filters, page, DEFAULT_PAGE_SIZE);

  const listQuery = useQuery({
    queryKey: ['conversations', qs],
    queryFn: () => apiClient.get<ListResponse>(`/admin/conversations?${qs}`),
    placeholderData: keepPreviousData,
  });

  const totalPages = listQuery.data
    ? Math.max(1, Math.ceil(listQuery.data.total / listQuery.data.pageSize))
    : 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Conversas</h1>
        <p className="text-sm text-muted-foreground">
          Explore conversas, filtre por status/campanha/texto e inspecione o histórico.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[14rem,1fr] md:gap-3 lg:grid-cols-[18rem,1fr] lg:gap-4">
        <ConversationFilters
          value={filters}
          onApply={(next) => {
            setFilters(next);
            setPage(1);
          }}
          onClear={() => {
            setFilters(EMPTY_FILTERS);
            setPage(1);
          }}
        />

        <Card>
          <CardContent className="p-0">
            {listQuery.isLoading ? (
              <div data-testid="conversations-skeleton" className="p-6">
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-8 animate-pulse rounded bg-muted" />
                  ))}
                </div>
              </div>
            ) : listQuery.error ? (
              <div className="p-6 text-sm text-destructive">
                {humanizeError(listQuery.error, 'Falha ao carregar conversas.')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table">
                  <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2">Telefone</th>
                      <th className="px-4 py-2">Campanha</th>
                      <th className="px-4 py-2">Status</th>
                      <th className="px-4 py-2">Mensagens</th>
                      <th className="px-4 py-2">Última msg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listQuery.data &&
                    Array.isArray(listQuery.data.items) &&
                    listQuery.data.items.length > 0 ? (
                      listQuery.data.items.map((c) => (
                        <tr
                          key={c.id}
                          data-testid={`conversation-row-${c.id}`}
                          className="cursor-pointer border-b hover:bg-accent/40 last:border-b-0"
                          onClick={() => navigate(`/conversations/${c.id}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') navigate(`/conversations/${c.id}`);
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={`Abrir conversa ${c.maskedPhone}`}
                        >
                          <td className="px-4 py-2 font-mono text-xs">{c.maskedPhone}</td>
                          <td className="px-4 py-2">{c.campaignName ?? '—'}</td>
                          <td className="px-4 py-2">
                            <StatusBadge status={c.status} />
                          </td>
                          <td className="px-4 py-2">{c.messageCount}</td>
                          <td className="px-4 py-2">{formatRelative(c.lastMessageAt)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                          Nenhuma conversa encontrada.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {listQuery.data && listQuery.data.total > 0 ? (
        <div className="flex items-center justify-end gap-2 text-sm">
          <span className="text-muted-foreground">
            Página {listQuery.data.page} de {totalPages} ({listQuery.data.total} total)
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default ConversationsPage;
